/**
 * RepeaterR — Add/Remove repeating entry blocks.
 *
 * Uses react-hook-form's useFieldArray. Each entry renders the entry_schema
 * fields via FieldRenderer. The entry's canonical_name uses [*] notation;
 * at render time we substitute the actual array index.
 */

import { useFieldArray, useFormContext } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import type { FieldDef, RendererProps } from '../types';
import { resolveLabel, deriveFieldName } from '../utils';

// Import FieldRenderer here — split file to avoid circular deps
// The actual import happens dynamically to break the circular dep:
// RepeaterR -> FieldRenderer -> RepeaterR
// We break this by accepting a FieldRenderer prop or using lazy import.
// Solution: accept the renderer as a render-prop.

import type { ReactNode } from 'react';

interface EntryRendererProps {
  entrySchema: FieldDef[];
  namePrefix: string;
  formId: string;
  pdfMode: boolean;
  index: number;
}

interface Props extends RendererProps {
  field: FieldDef;
  /** Injected by SchemaRenderer to avoid circular dependency */
  renderEntry: (props: EntryRendererProps) => ReactNode;
}

/**
 * Build a repeater entry heading from its template.
 *
 * `{index}` becomes the 1-based entry number; every other `{token}` is
 * substituted with the matching value from the entry (keyed by the field's
 * last canonical-name segment). Unfilled tokens collapse to nothing rather
 * than a literal placeholder, and a dangling separator left behind is
 * trimmed. Pure so it can be unit tested without mounting the component.
 *
 * BUG-4: the old code replaced every non-index `{token}` with the literal
 * string "Entry N", so "Companion {index}: {family_name} {given_names}"
 * rendered as "Companion 1: Entry 1 Entry 1".
 */
export function formatEntryLabel(
  template: string | undefined,
  index: number,
  values: Record<string, unknown>,
  fallbackLabel: string,
): string {
  if (!template) return `${fallbackLabel} ${index + 1}`;
  return template
    .replace(/\{(\w+)\}/g, (_match, token: string) => {
      if (token === 'index') return String(index + 1);
      const value = values[token];
      return value == null || value === '' ? '' : String(value);
    })
    .replace(/\s+/g, ' ')
    .replace(/[\s:–-]+$/, '')
    .trim();
}

export function RepeaterR({ field, formId, pdfMode = false, namePrefix, audience, renderEntry }: Props) {
  const { control, watch } = useFormContext();
  const fieldName = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);

  const { fields: entries, append, remove } = useFieldArray({
    control,
    name: fieldName,
  });

  const watchedEntries = watch(fieldName) as Record<string, unknown>[] | undefined;

  const entrySchema = field.entry_schema ?? [];
  const minCount = field.min_count ?? 0;
  const maxCount = field.max_count ?? null;
  const canAdd = maxCount === null || entries.length < maxCount;
  const canRemove = entries.length > minCount;

  function buildEntryLabel(index: number): string {
    return formatEntryLabel(
      field.entry_label_template,
      index,
      watchedEntries?.[index] ?? {},
      label,
    );
  }

  if (pdfMode) {
    return (
      <div className="space-y-4">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        {entries.length === 0 ? (
          <p className="text-sm text-slate-400 italic">None</p>
        ) : (
          entries.map((entry, index) => (
            <div key={entry.id} className="pl-3 border-l-2 border-slate-200 space-y-3">
              <span className="text-xs font-semibold text-slate-500">{buildEntryLabel(index)}</span>
              {renderEntry({
                entrySchema,
                namePrefix: `${fieldName}.${index}`,
                formId,
                pdfMode,
                index,
              })}
            </div>
          ))
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        {entries.length > 0 && (
          <span className="text-xs text-slate-400">{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
        )}
      </div>

      {entries.length === 0 && minCount === 0 && (
        <p className="text-sm text-slate-400 italic">No entries yet. Click below to add one.</p>
      )}

      {entries.map((entry, index) => (
        <div
          key={entry.id}
          className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-4"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-600">{buildEntryLabel(index)}</span>
            {canRemove && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors"
                aria-label={`Remove ${buildEntryLabel(index)}`}
              >
                <Trash2 size={13} /> Remove
              </button>
            )}
          </div>
          <div className="space-y-4">
            {renderEntry({
              entrySchema,
              namePrefix: `${fieldName}.${index}`,
              formId,
              pdfMode,
              index,
            })}
          </div>
        </div>
      ))}

      {canAdd && (
        <button
          type="button"
          onClick={() => append({})}
          className="flex items-center gap-2 text-sm font-medium text-[#2CABEA] hover:text-[#1a8fc0] transition-colors border border-dashed border-[#2CABEA]/40 hover:border-[#2CABEA] rounded-lg px-4 py-2.5 w-full justify-center"
        >
          <Plus size={15} />
          Add {label.toLowerCase()}
        </button>
      )}

      {field.help_text && (
        <p className="form-hint">{field.help_text}</p>
      )}
    </div>
  );
}
